/**
 * Sistema di logging centralizzato per Pasticceria Gestionale
 * Traccia tutto quello che succede nell'app per debug e monitoraggio
 */

class Logger {
    constructor() {
        this.logs = []
        this.maxLogs = 1000 // Limite massimo di log in memoria
        this.isEnabled = true
        this.logLevels = {
            ERROR: 'ERROR',
            WARN: 'WARN',
            INFO: 'INFO',
            DEBUG: 'DEBUG',
            TRACE: 'TRACE'
        }

        // Colori per console
        this.colors = {
            ERROR: '#ff4757',
            WARN: '#ffa502',
            INFO: '#3742fa',
            DEBUG: '#2ed573',
            TRACE: '#747d8c'
        }

        // Carica logs salvati
        this.loadLogs()

        // Cattura errori non gestiti
        this.setupGlobalErrorHandling()

        this.info('SYSTEM', 'Logger initialized', { timestamp: new Date().toISOString() })
    }

    // Crea un log entry
    createLogEntry(level, component, message, data = null) {
        const logEntry = {
            id: Date.now() + Math.random(),
            timestamp: new Date().toISOString(),
            level,
            component,
            message,
            data,
            url: window.location.href,
            userAgent: navigator.userAgent.substring(0, 100)
        }

        // Aggiungi alla lista
        this.logs.unshift(logEntry)

        // Mantieni solo gli ultimi N log
        if (this.logs.length > this.maxLogs) {
            this.logs = this.logs.slice(0, this.maxLogs)
        }

        // Salva in localStorage
        this.saveLogs()

        // Log in console con colore
        if (this.isEnabled) {
            const consoleMethod = level === 'ERROR' ? 'error' :
                level === 'WARN' ? 'warn' :
                    level === 'DEBUG' ? 'debug' : 'log'

            console.group(`%c[${level}] ${component}`, `color: ${this.colors[level]}; font-weight: bold`)
            console[consoleMethod](message)
            if (data) {
                console.log('Data:', data)
            }
            console.log(`Time: ${new Date().toLocaleTimeString()}`)
            console.log(`URL: ${window.location.href}`)
            console.groupEnd()
        }

        return logEntry
    }

    // Metodi di logging
    error(component, message, data) {
        return this.createLogEntry(this.logLevels.ERROR, component, message, data)
    }

    warn(component, message, data) {
        return this.createLogEntry(this.logLevels.WARN, component, message, data)
    }

    info(component, message, data) {
        return this.createLogEntry(this.logLevels.INFO, component, message, data)
    }

    debug(component, message, data) {
        return this.createLogEntry(this.logLevels.DEBUG, component, message, data)
    }

    trace(component, message, data) {
        return this.createLogEntry(this.logLevels.TRACE, component, message, data)
    }

    // Gestione errori globali
    setupGlobalErrorHandling() {
        // Errori JavaScript
        window.onerror = (message, source, lineno, colno, error) => {
            this.error('GLOBAL_ERROR', `JavaScript Error: ${message}`, {
                source,
                line: lineno,
                column: colno,
                error: error?.stack
            })
        }

        // Promise rejections non gestite
        window.addEventListener('unhandledrejection', (event) => {
            this.error('PROMISE_REJECTION', `Unhandled Promise Rejection: ${event.reason}`, {
                promise: event.promise,
                reason: event.reason
            })
        })

        // Errori di risorse
        window.addEventListener('error', (event) => {
            if (event.target !== window) {
                this.error('RESOURCE_ERROR', `Resource Error: ${event.target.src || event.target.href}`, {
                    element: event.target.tagName,
                    source: event.target.src || event.target.href
                })
            }
        }, true)
    }

    // Salva logs in localStorage e invia al backend
    saveLogs() {
        try {
            const logsToSave = this.logs.slice(0, 500) // Salva solo gli ultimi 500
            localStorage.setItem('app_logs', JSON.stringify(logsToSave))

            // Invia logs al backend ogni 50 nuovi log
            if (this.logs.length > 0 && this.logs.length % 50 === 0) {
                this.sendLogsToBackend()
            }
        } catch (error) {
            console.error('Failed to save logs:', error)
        }
    }

    // Invia logs al backend
    sendLogsToBackend() {
        try {
            // Usa fetch per evitare dipendenze circolari
            const recentLogs = this.logs.slice(0, 100) // Ultimi 100 log

            fetch('/api/logs/frontend', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ logs: recentLogs })
            }).catch(error => {
                // Silently fail per evitare loop infiniti di log
                console.debug('Failed to send logs to backend:', error)
            })
        } catch (error) {
            console.debug('Error sending logs to backend:', error)
        }
    }

    // Carica logs da localStorage
    loadLogs() {
        try {
            const saved = localStorage.getItem('app_logs')
            if (saved) {
                this.logs = JSON.parse(saved)
            }
        } catch (error) {
            console.error('Failed to load logs:', error)
            this.logs = []
        }
    }

    // Ottieni logs filtrati
    getLogs(filter = {}) {
        let filtered = [...this.logs]

        if (filter.level) {
            filtered = filtered.filter(log => log.level === filter.level)
        }

        if (filter.component) {
            filtered = filtered.filter(log =>
                log.component.toLowerCase().includes(filter.component.toLowerCase())
            )
        }

        if (filter.message) {
            filtered = filtered.filter(log =>
                log.message.toLowerCase().includes(filter.message.toLowerCase())
            )
        }

        if (filter.since) {
            const since = new Date(filter.since)
            filtered = filtered.filter(log => new Date(log.timestamp) >= since)
        }

        return filtered.slice(0, filter.limit || 100)
    }

    // Esporta logs come JSON
    exportLogs() {
        const data = {
            exported: new Date().toISOString(),
            app: 'Pasticceria Gestionale',
            version: '1.0.0',
            logs: this.logs
        }

        const blob = new Blob([JSON.stringify(data, null, 2)], {
            type: 'application/json'
        })

        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `app-logs-${new Date().toISOString().split('T')[0]}.json`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)

        this.info('LOGGER', 'Logs exported successfully')
    }

    // Pulisci logs
    clearLogs() {
        this.logs = []
        localStorage.removeItem('app_logs')
        this.info('LOGGER', 'Logs cleared')
    }

    // Ottieni statistiche
    getStats() {
        const stats = {
            total: this.logs.length,
            byLevel: {},
            byComponent: {},
            recentErrors: 0,
            oldestLog: null,
            newestLog: null
        }

        // Conta per livello
        this.logs.forEach(log => {
            stats.byLevel[log.level] = (stats.byLevel[log.level] || 0) + 1
            stats.byComponent[log.component] = (stats.byComponent[log.component] || 0) + 1
        })

        // Errori recenti (ultime 24 ore)
        const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000)
        stats.recentErrors = this.logs.filter(log =>
            log.level === 'ERROR' && new Date(log.timestamp) >= yesterday
        ).length

        // Date
        if (this.logs.length > 0) {
            stats.oldestLog = this.logs[this.logs.length - 1].timestamp
            stats.newestLog = this.logs[0].timestamp
        }

        return stats
    }

    // Abilita/disabilita logging
    toggle(enabled = !this.isEnabled) {
        this.isEnabled = enabled
        this.info('LOGGER', `Logging ${enabled ? 'enabled' : 'disabled'}`)
    }
}

// Istanza singola
const logger = new Logger()

// Aggiungi anche performance monitoring
if (window.performance && window.performance.mark) {
    logger.info('PERFORMANCE', 'Performance monitoring available')

    // Log navigation timing
    window.addEventListener('load', () => {
        setTimeout(() => {
            const perfData = performance.getEntriesByType('navigation')[0]
            if (perfData) {
                logger.info('PERFORMANCE', 'Page load completed', {
                    domContentLoaded: perfData.domContentLoadedEventEnd - perfData.domContentLoadedEventStart,
                    loadComplete: perfData.loadEventEnd - perfData.loadEventStart,
                    totalTime: perfData.loadEventEnd - perfData.fetchStart
                })
            }
        }, 0)
    })
}

export default logger
